// NEW_FILE_JUSTIFIED: state-machine hook bundling upload, action dispatch, and status subscription.
import { useState } from 'react';
import { useMutation, useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is the standard Convex React client package required for useMutation/useQuery — not a convex backend module
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig — all hooks in src/hooks/ use this relative path
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import
import type { IUseAiPolicyExtractionResult, TExtractionStage } from '@/@types/insuranceBuilder';
import { useOrgContext } from './useOrgContext';

export function useAiPolicyExtraction(): IUseAiPolicyExtractionResult {
  const { currentOrg } = useOrgContext();
  const createUploadUrl = useMutation(api.storage.createUploadUrl);
  const startExtraction = useMutation(api['insurance/aiExtract'].startExtraction);

  const [stage, setStage] = useState<TExtractionStage>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [extractionId, setExtractionId] = useState<Id<'aiPolicyExtractions'> | null>(null);

  const status = useQuery(
    api['insurance/aiExtract'].getExtractionStatus,
    extractionId ? { extractionId } : 'skip'
  );

  // Reflect server status back into local stage (render-time sync — no useEffect needed).
  if (status) {
    if (
      status.status === 'pending' &&
      stage !== 'queued' &&
      stage !== 'success' &&
      stage !== 'error'
    ) {
      setStage('queued');
    } else if (
      status.status === 'processing' &&
      stage !== 'processing' &&
      stage !== 'success' &&
      stage !== 'error'
    ) {
      setStage('processing');
    } else if (status.status === 'success' && stage !== 'success') {
      setStage('success');
    } else if (status.status === 'error' && stage !== 'error') {
      setStage('error');
      setErrorMessage(status.error ?? 'Extraction failed');
    }
  }

  const startUpload: IUseAiPolicyExtractionResult['startUpload'] = async (file, opts) => {
    if (!currentOrg) {
      setStage('error');
      setErrorMessage('No active organization');
      return;
    }
    try {
      setStage('uploading');
      setErrorMessage(null);
      const uploadUrl = await createUploadUrl({});
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      const { storageId } = (await res.json()) as { storageId: Id<'_storage'> }; // AS_JUSTIFIED: Convex upload response always returns { storageId: Id<'_storage'> }

      const id = await startExtraction({
        orgId: currentOrg._id,
        storageId,
        providerId: opts.providerId,
        name: opts.name,
      });
      setExtractionId(id);
      setStage('queued');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setStage('error');
      setErrorMessage(msg);
    }
  };

  const reset = () => {
    setStage('idle');
    setErrorMessage(null);
    setExtractionId(null);
  };

  return {
    stage,
    errorMessage,
    resultTypeId: status?.resultTypeId ?? null,
    startUpload,
    reset,
  };
}
